import React from 'react';
import { Zap, Flame } from 'lucide-react';

// Définition des offres tarifaires disponibles
const OFFRES_PARTICULIER = [
  { id: 'standard', label: 'Standard (Base / HP-HC / Super Creuse)' },
  { id: 'tempo', label: 'Tempo (6 plages couleurs)' },
  { id: 'zen', label: 'Zen Week-End (4 plages)' },
];

/**
 * Section Particulier : informations site + tableau de consommation simplifié.
 *
 * Le tableau affiche dynamiquement les postes selon l'offre tarifaire choisie :
 *   - "standard" : Base, Heure Super Pleine, HP, HC, Super Creuse
 *   - "tempo"    : 6 plages (Rouge HP/HC, Blanc HP/HC, Bleu HP/HC)
 *   - "zen"      : 4 plages (HP/HC Semaine, HP/HC Week-End)
 */
const ParticulierSection = ({
  energyType,
  particulierPriceDisplay,
  setParticulierPriceDisplay,
  // Identité site
  clientName,
  setClientName,
  adresseSite,
  setAdresseSite,
  pdlParticulier,
  setPdlParticulier,
  fournisseurActuel,
  setFournisseurActuel,
  fournisseurFutur,
  setFournisseurFutur,
  // Offre tarifaire
  offreParticulier,
  setOffreParticulier,
  // Élec
  postesAffichesParticulier,
  handlePosteParticulierChange,
  abonnementParticulier,
  setAbonnementParticulier,
  totalConsoParticulierKWh,
  totalParticulierActuel,
  totalParticulierFutur,
  // Gaz
  consoGazParticulier,
  setConsoGazParticulier,
  abonnementGazParticulier,
  setAbonnementGazParticulier,
  consoGazParticulierKWh,
  totalGazParticulierActuel,
  totalGazParticulierFutur,
  formatNumber,
  calculateBudget,
}) => {
  const priceLabel = particulierPriceDisplay === 'ht' ? 'HT' : 'TTC';

  return (
    <>
      {/* SECTION INFOS CLIENT PARTICULIER */}
      <section className="card" data-testid="particulier-info-section">
        <h2 className="section-title">Informations Client Particulier</h2>

        <div className="form-grid form-grid-2">
          <div className="form-group">
            <label htmlFor="clientNameParticulier">Nom du Client</label>
            <input
              type="text"
              id="clientNameParticulier"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Entrez le nom du client"
              data-testid="client-name-particulier-input"
            />
          </div>
          <div className="form-group">
            <label>Affichage des prix</label>
            <div className="ht-ttc-toggle-container">
              <button
                className={`ht-ttc-toggle-btn ${particulierPriceDisplay === 'ht' ? 'active' : ''}`}
                onClick={() => setParticulierPriceDisplay('ht')}
                data-testid="particulier-ht-btn"
              >
                HT
              </button>
              <button
                className={`ht-ttc-toggle-btn ${particulierPriceDisplay === 'ttc' ? 'active' : ''}`}
                onClick={() => setParticulierPriceDisplay('ttc')}
                data-testid="particulier-ttc-btn"
              >
                TTC
              </button>
            </div>
          </div>
        </div>

        {/* Adresse + PDL */}
        <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label htmlFor="adresseSite">Adresse du site de consommation</label>
            <input
              type="text"
              id="adresseSite"
              value={adresseSite}
              onChange={(e) => setAdresseSite(e.target.value)}
              placeholder="123 rue Example, 75001 Paris"
              data-testid="adresse-site-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="pdlParticulier">PDL (Point de Livraison)</label>
            <input
              type="text"
              id="pdlParticulier"
              value={pdlParticulier}
              onChange={(e) => setPdlParticulier(e.target.value)}
              placeholder="14 chiffres (ex: 12345678901234)"
              data-testid="pdl-particulier-input"
            />
          </div>
        </div>

        {/* Fournisseurs */}
        <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
          <div className="form-group">
            <label htmlFor="fournisseurActuel">Fournisseur actuel</label>
            <input
              type="text"
              id="fournisseurActuel"
              value={fournisseurActuel}
              onChange={(e) => setFournisseurActuel(e.target.value)}
              placeholder="Ex: EDF"
              data-testid="fournisseur-actuel-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="fournisseurFutur">Fournisseur futur</label>
            <input
              type="text"
              id="fournisseurFutur"
              value={fournisseurFutur}
              onChange={(e) => setFournisseurFutur(e.target.value)}
              placeholder="Ex: PRIMEO"
              data-testid="fournisseur-futur-input"
            />
          </div>
        </div>

        {/* Offre tarifaire (Élec uniquement) */}
        {energyType === 'electricity' && (
          <div className="form-grid form-grid-2" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label htmlFor="offreParticulier">Offre tarifaire</label>
              <select
                id="offreParticulier"
                value={offreParticulier}
                onChange={(e) => setOffreParticulier(e.target.value)}
                data-testid="offre-particulier-select"
              >
                {OFFRES_PARTICULIER.map(offre => (
                  <option key={offre.id} value={offre.id}>{offre.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </section>

      {/* TABLEAU CONSOMMATION + COMPARATIF */}
      <section className="card particulier-conso-section" data-testid="particulier-conso-section">
        <h2 className="section-title">
          {energyType === 'electricity' ? (
            <><Zap size={20} className="section-icon" style={{ color: '#3B82F6' }} /> Comparatif Budget Électricité</>
          ) : (
            <><Flame size={20} className="section-icon" style={{ color: '#F97316' }} /> Comparatif Budget Gaz</>
          )}
          <span className="annual-badge">Prix {priceLabel}</span>
        </h2>

        <div className="particulier-price-toggle-header">
          <span>Affichage : </span>
          <div className="ht-ttc-toggle-container inline">
            <button
              className={`ht-ttc-toggle-btn ${particulierPriceDisplay === 'ht' ? 'active' : ''}`}
              onClick={() => setParticulierPriceDisplay('ht')}
            >
              HT
            </button>
            <button
              className={`ht-ttc-toggle-btn ${particulierPriceDisplay === 'ttc' ? 'active' : ''}`}
              onClick={() => setParticulierPriceDisplay('ttc')}
            >
              TTC
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="data-table" data-testid="particulier-conso-table">
            <thead>
              {/* Bandeau "Fournisseur Actuel | Fournisseur Futur" au-dessus des colonnes budget */}
              <tr className="fournisseur-row">
                <th colSpan={2}></th>
                <th colSpan={2} style={{ background: '#F1F5F9', color: '#475569', fontWeight: 600, textAlign: 'center' }}>
                  {fournisseurActuel ? `Fournisseur : ${fournisseurActuel}` : 'Fournisseur actuel'}
                </th>
                <th colSpan={2} style={{ background: '#ECFDF5', color: '#166534', fontWeight: 600, textAlign: 'center' }}>
                  {fournisseurFutur ? `Fournisseur : ${fournisseurFutur}` : 'Fournisseur futur'}
                </th>
              </tr>
              <tr>
                <th>Poste</th>
                <th>Conso (kWh)</th>
                <th>Prix Actuel (€/kWh)</th>
                <th>Total Actuel {priceLabel} (€)</th>
                <th>Prix Futur (€/kWh)</th>
                <th>Total Futur {priceLabel} (€)</th>
              </tr>
            </thead>
            <tbody>
              {energyType === 'electricity' ? (
                <>
                  {postesAffichesParticulier.map((poste) => (
                    <tr key={poste.id}>
                      <td className="poste-label">{poste.label}</td>
                      <td>
                        <input
                          type="number"
                          value={poste.conso}
                          onChange={(e) => handlePosteParticulierChange(poste.id, 'conso', e.target.value)}
                          placeholder="0"
                          step="1"
                          data-testid={`particulier-conso-${poste.id}`}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={poste.prixActuel}
                          onChange={(e) => handlePosteParticulierChange(poste.id, 'prixActuel', e.target.value)}
                          placeholder="0.0000"
                          step="0.0001"
                          data-testid={`particulier-prix-actuel-${poste.id}`}
                        />
                      </td>
                      <td className="calculated">{formatNumber(calculateBudget(poste.conso, poste.prixActuel))}</td>
                      <td>
                        <input
                          type="number"
                          value={poste.prixFutur}
                          onChange={(e) => handlePosteParticulierChange(poste.id, 'prixFutur', e.target.value)}
                          placeholder="0.0000"
                          step="0.0001"
                          data-testid={`particulier-prix-futur-${poste.id}`}
                        />
                      </td>
                      <td className="calculated">{formatNumber(calculateBudget(poste.conso, poste.prixFutur))}</td>
                    </tr>
                  ))}
                  <tr className="abonnement-row">
                    <td className="poste-label" style={{ fontWeight: '600' }}>Abonnement annuel</td>
                    <td className="calculated" style={{ textAlign: 'center', color: '#64748B' }}>-</td>
                    <td>
                      <input
                        type="number"
                        value={abonnementParticulier.prixActuel}
                        onChange={(e) => setAbonnementParticulier({ ...abonnementParticulier, prixActuel: e.target.value })}
                        placeholder="€/an"
                        step="0.01"
                        data-testid="particulier-abo-actuel"
                      />
                    </td>
                    <td className="calculated">{formatNumber(parseFloat(abonnementParticulier.prixActuel) || 0)}</td>
                    <td>
                      <input
                        type="number"
                        value={abonnementParticulier.prixFutur}
                        onChange={(e) => setAbonnementParticulier({ ...abonnementParticulier, prixFutur: e.target.value })}
                        placeholder="€/an"
                        step="0.01"
                        data-testid="particulier-abo-futur"
                      />
                    </td>
                    <td className="calculated">{formatNumber(parseFloat(abonnementParticulier.prixFutur) || 0)}</td>
                  </tr>
                </>
              ) : (
                <>
                  <tr>
                    <td className="poste-label">Consommation Gaz</td>
                    <td>
                      <input
                        type="number"
                        value={consoGazParticulier.conso}
                        onChange={(e) => setConsoGazParticulier({ ...consoGazParticulier, conso: e.target.value })}
                        placeholder="0"
                        step="1"
                        data-testid="particulier-conso-gaz"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={consoGazParticulier.prixActuel}
                        onChange={(e) => setConsoGazParticulier({ ...consoGazParticulier, prixActuel: e.target.value })}
                        placeholder="0.0000"
                        step="0.0001"
                        data-testid="particulier-prix-actuel-gaz"
                      />
                    </td>
                    <td className="calculated">{formatNumber(consoGazParticulierKWh * (parseFloat(consoGazParticulier.prixActuel) || 0))}</td>
                    <td>
                      <input
                        type="number"
                        value={consoGazParticulier.prixFutur}
                        onChange={(e) => setConsoGazParticulier({ ...consoGazParticulier, prixFutur: e.target.value })}
                        placeholder="0.0000"
                        step="0.0001"
                        data-testid="particulier-prix-futur-gaz"
                      />
                    </td>
                    <td className="calculated">{formatNumber(consoGazParticulierKWh * (parseFloat(consoGazParticulier.prixFutur) || 0))}</td>
                  </tr>
                  <tr className="abonnement-row">
                    <td className="poste-label" style={{ fontWeight: '600' }}>Abonnement annuel</td>
                    <td className="calculated" style={{ textAlign: 'center', color: '#64748B' }}>-</td>
                    <td>
                      <input
                        type="number"
                        value={abonnementGazParticulier.prixActuel}
                        onChange={(e) => setAbonnementGazParticulier({ ...abonnementGazParticulier, prixActuel: e.target.value })}
                        placeholder="€/an"
                        step="0.01"
                        data-testid="particulier-abo-gaz-actuel"
                      />
                    </td>
                    <td className="calculated">{formatNumber(parseFloat(abonnementGazParticulier.prixActuel) || 0)}</td>
                    <td>
                      <input
                        type="number"
                        value={abonnementGazParticulier.prixFutur}
                        onChange={(e) => setAbonnementGazParticulier({ ...abonnementGazParticulier, prixFutur: e.target.value })}
                        placeholder="€/an"
                        step="0.01"
                        data-testid="particulier-abo-gaz-futur"
                      />
                    </td>
                    <td className="calculated">{formatNumber(parseFloat(abonnementGazParticulier.prixFutur) || 0)}</td>
                  </tr>
                </>
              )}

              <tr className="total-row">
                <td>Total {priceLabel}</td>
                <td className="total-value">
                  {energyType === 'electricity'
                    ? `${formatNumber(totalConsoParticulierKWh)} kWh`
                    : `${formatNumber(consoGazParticulierKWh)} kWh`}
                </td>
                <td className="total-value avg-price">-</td>
                <td className="total-value">
                  {energyType === 'electricity'
                    ? formatNumber(totalParticulierActuel)
                    : formatNumber(totalGazParticulierActuel)} €
                </td>
                <td className="total-value avg-price">-</td>
                <td className="total-value">
                  {energyType === 'electricity'
                    ? formatNumber(totalParticulierFutur)
                    : formatNumber(totalGazParticulierFutur)} €
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Comparatif visuel */}
        <div className="particulier-comparison" style={{ marginTop: '1.5rem', padding: '1rem', background: '#F8FAFC', borderRadius: '8px' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#1E293B' }}>
            Comparatif Budget {priceLabel}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ padding: '1rem', background: '#fff', borderRadius: '8px', border: '2px solid #E2E8F0' }}>
              {fournisseurActuel && (
                <div style={{ fontSize: '0.75rem', color: '#475569', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Fournisseur : {fournisseurActuel}
                </div>
              )}
              <div style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '0.5rem' }}>Budget Actuel {priceLabel}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1E293B' }}>
                {formatNumber(energyType === 'electricity' ? totalParticulierActuel : totalGazParticulierActuel)} €/an
              </div>
            </div>
            <div style={{ padding: '1rem', background: energyType === 'electricity' ? '#EFF6FF' : '#FFF7ED', borderRadius: '8px', border: `2px solid ${energyType === 'electricity' ? '#3B82F6' : '#F97316'}` }}>
              {fournisseurFutur && (
                <div style={{ fontSize: '0.75rem', color: '#166534', marginBottom: '0.25rem', fontWeight: 600 }}>
                  Fournisseur : {fournisseurFutur}
                </div>
              )}
              <div style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '0.5rem' }}>Budget Futur {priceLabel}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: energyType === 'electricity' ? '#3B82F6' : '#F97316' }}>
                {formatNumber(energyType === 'electricity' ? totalParticulierFutur : totalGazParticulierFutur)} €/an
              </div>
            </div>
          </div>
          {(() => {
            const actuel = energyType === 'electricity' ? totalParticulierActuel : totalGazParticulierActuel;
            const futur = energyType === 'electricity' ? totalParticulierFutur : totalGazParticulierFutur;
            const diff = futur - actuel;
            const pct = actuel > 0 ? ((diff / actuel) * 100) : 0;
            if (Math.abs(diff) <= 0.01) return null;
            return (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                background: diff < 0 ? '#D1FAE5' : '#FEE2E2',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <strong style={{ color: diff < 0 ? '#166534' : '#991B1B', fontSize: '1.1rem' }}>
                  {diff < 0 ? '↓' : '↑'} {formatNumber(Math.abs(diff))} € ({formatNumber(Math.abs(pct))}%)
                  {diff < 0 ? " d'économie" : ' de surcoût'} par an
                </strong>
              </div>
            );
          })()}
        </div>
      </section>
    </>
  );
};

export default ParticulierSection;
